import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import GiroInput, { isValidGiro } from './GiroInput';
import TextInput, { OptionalTextInput, isValidText } from './TextInput';
import SelectInput from './SelectInput';
import { ClearingInput, BankAccountNumberInput, checkBankAccount } from './BankAccountInput';
import CurrencySelect, { isForeign, normalizeCurrency } from './CurrencySelect';
import PaymentCodeSelect from './PaymentCodeSelect';
import CountrySelect from './CountrySelect';
import { Supplier } from './Types';
import { apiCreateSupplier, apiReadDocument } from './Api';
import { addToCallChain } from './CallCenter';

// Where a domestic supplier is paid: a giro, or an ordinary bank account as an utlägg is — the
// same choice the reimbursee dialog offers, the other way round.
type Route = 'giro' | 'bank'
const ROUTES: { value: Route, name: string }[] = [
  { value: 'giro', name: 'Bankgiro eller plusgiro' },
  { value: 'bank', name: 'Bankkonto' },
]

// A foreign supplier is paid by IBAN rather than giro. Visma accepts one without an IBAN (the
// invoice just can't be paid until it has one), so we ask for it but don't demand it — a
// sub-treasurer shouldn't be blocked by an invoice that omits it.
const normalizeAccountField = (value: string): string => value.replace(/\s/g, '').toUpperCase()

// "Giro, IBAN och BIC" — a Swedish invoice can state all three, so the note needs a real list
// rather than repeated "och".
const swedishList = (items: string[]): string =>
  items.length <= 1 ? (items[0] ?? '')
                    : `${items.slice(0, -1).join(', ')} och ${items[items.length - 1]}`

function SupplierCreationDialog({currencyCode, usesOcr, attachmentId, onCreate, onCancel}: {
  // Preselected from the invoice draft: the supplier must end up in the invoice's currency, or
  // Visma will refuse to book on it.
  currencyCode?: string,
  // Whether the invoice being booked pays by OCR. Carried into the supplier so the common case
  // needs no correction later; the backend matches the setting to the invoice at registration
  // either way, so getting it wrong here costs one extra write, not a failed booking.
  usesOcr?: boolean,
  // The invoice being booked. Its own text usually states where to pay — Visma's scan never
  // passes that on, so it is retyped by hand today.
  attachmentId?: string,
  onCreate: (args: {newSuppliers: Supplier[], newSupplierId: string, currencyCode: string}) => void,
  onCancel: () => void
}) {

  const [supplierName, setSupplierName] = useState('')
  const [route, setRoute] = useState<Route>('giro')
  const [giroNumber, setGiroNumber] = useState('')
  const [clearing, setClearing] = useState('')
  const [account, setAccount] = useState('')
  const [currency, setCurrency] = useState(normalizeCurrency(currencyCode))
  const [iban, setIban] = useState('')
  const [bic, setBic] = useState('')
  const [address, setAddress] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [paymentCode, setPaymentCode] = useState<number | null>(null)
  const [readFromInvoice, setReadFromInvoice] = useState<string[]>([])

  // Read the account off the invoice and offer it, rather than making the user copy digits across
  // from the document. Only values the backend is certain of come back, so a field left empty here
  // simply means the invoice didn't say — the same position the user is in today.
  useEffect(() => {
    if (attachmentId === undefined) return
    let current = true
    apiReadDocument(attachmentId).then(({giro, iban, bic}) => {
      if (!current) return
      // Never overwrite what the user has already typed: the request is in flight while the dialog
      // is usable, and their own typing outranks anything read off the page.
      if (giro !== undefined) setGiroNumber(typed => typed || giro)
      if (iban !== undefined) setIban(typed => typed || iban)
      if (bic !== undefined) setBic(typed => typed || bic)
      setReadFromInvoice([
        giro !== undefined ? 'Giro' : null,
        iban !== undefined ? 'IBAN' : null,
        bic !== undefined ? 'BIC' : null,
      ].filter((label): label is string => label !== null))
    // A document Eko can't read is not an error worth showing — the fields stay empty.
    }).catch(() => {})
    return () => { current = false }
  }, [attachmentId])

  const foreign = isForeign(currency)
  // The IBAN begins with the country's ISO code, so a pasted IBAN fills the country in — one less
  // field to type, still editable, and still asked for when the invoice states no IBAN.
  const countryFromIban = normalizeAccountField(iban).slice(0, 2)
  const effectiveCountry = (country || (/^[A-Z]{2}$/.test(countryFromIban) ? countryFromIban : '')).toUpperCase()

  // A supplier's nationality is spread over several fields, and two pairs of them state the *same*
  // fact, so they can be checked against each other rather than merely hoped about:
  //   - an IBAN's first two letters are the country of the account it belongs to, and Eko sends the
  //     chosen Land as Visma's BankCountryCode — the country of the account being paid;
  //   - a BIC's fifth and sixth characters are the country of the bank.
  // Neither disagreement is necessarily an error: a supplier in one country may legitimately be
  // paid to an account in another. But it is far more often a mistyped code or an IBAN pasted from
  // the wrong invoice, and either sends a payment abroad to the wrong place — so say so and let the
  // user decide. The currency is deliberately not checked against any of them: a Norwegian supplier
  // invoicing a Swedish club in SEK is ordinary, not a mistake.
  const bicCountry = normalizeAccountField(bic).slice(4, 6)
  const isCode = (value: string) => /^[A-Z]{2}$/.test(value)
  const countryWarnings: string[] = []
  if (isCode(countryFromIban) && isCode(effectiveCountry) && countryFromIban !== effectiveCountry) {
    countryWarnings.push(`IBAN tillhör ${countryFromIban}, inte ${effectiveCountry}`)
  }
  if (isCode(bicCountry) && isCode(countryFromIban) && bicCountry !== countryFromIban) {
    countryWarnings.push(`BIC tillhör ${bicCountry}, inte ${countryFromIban}`)
  }

  function createSupplier() {
    const data = foreign
      ? {name: supplierName, currency_code: currency,
         iban: normalizeAccountField(iban), bic: normalizeAccountField(bic),
         address_1: address, postal_code: postalCode, city, country_code: effectiveCountry,
         payment_code: paymentCode!, uses_ocr: !!usesOcr}
      : route === 'bank'
      // No uses_ocr: a deposit to a bank account carries a message, never an OCR number.
      ? {name: supplierName, clearing_number: clearing, bank_account_number: account, currency_code: currency}
      : {name: supplierName, giro_number: giroNumber, currency_code: currency,
         uses_ocr: !!usesOcr}
    addToCallChain(
      async () => {
        const responseData = await apiCreateSupplier(data)
        onCreate({newSuppliers: responseData.suppliers, newSupplierId: responseData.new_supplier_id,
                  currencyCode: currency})
      },
      true
    )
  }

  // Visma requires a full postal address on a foreign supplier — an international payment names
  // the beneficiary's address — and rejects the create outright without it.
  // Bank-aware validation over clearing and account together, as for an utlägg: the account's rules
  // depend on the clearing number's bank. Only shown once both have content.
  const bankCheck = checkBankAccount(clearing, account)
  const showBankStatus = clearing.trim() !== '' && account.trim() !== ''
  const isValid = isValidText(supplierName) && (foreign
    ? isValidText(address) && isValidText(postalCode) && isValidText(city)
      && /^[A-Z]{2}$/.test(effectiveCountry) && paymentCode !== null
    : route === 'bank' ? bankCheck.ok : isValidGiro(giroNumber))

  return (
    <>
    <div id="div-recognize-overlay"></div>
    <div id="div-new-supplier">
      {foreign && countryWarnings.length > 0 &&
        // A warning, not a block: paying a supplier in one country to an account in another is
        // unusual but legitimate, and refusing it would be worse than asking.
        <div className="country-warning">
          {countryWarnings.join('; ')} — kontrollera att det stämmer.
        </div>}
      {readFromInvoice.length > 0 &&
        // Say where the value came from. A prefilled account looks exactly as settled as a typed
        // one, so the user is told to give it a glance rather than left to assume Eko knew.
        <div className="read-from-invoice">
          {swedishList(readFromInvoice)} {readFromInvoice.length > 1 ? 'är avlästa' : 'är avläst'} från
          fakturan — kontrollera gärna.
        </div>}
      <TextInput id="new-supplier-name" label="Namn" inputProps={{'maxLength': "50"}} value={supplierName} onChange={setSupplierName}/>
      <CurrencySelect id="new-supplier-currency" label="Valuta" value={currency} onChange={setCurrency}/>
      {foreign
        ? <>
            <TextInput id="new-supplier-address" label="Adress" inputProps={{'maxLength': "50"}} value={address} onChange={setAddress}/>
            <TextInput id="new-supplier-postal-code" label="Postnummer" inputProps={{'maxLength': "10"}} value={postalCode} onChange={setPostalCode}/>
            <TextInput id="new-supplier-city" label="Ort" inputProps={{'maxLength': "50"}} value={city} onChange={setCity}/>
            <CountrySelect id="new-supplier-country" label="Land"
                           value={effectiveCountry} onChange={setCountry}/>
            <OptionalTextInput id="new-supplier-iban" label="IBAN" inputProps={{'maxLength': "34"}} value={iban} onChange={setIban}/>
            <OptionalTextInput id="new-supplier-bic" label="BIC/SWIFT" inputProps={{'maxLength': "11"}} value={bic} onChange={setBic}/>
            <PaymentCodeSelect id="new-supplier-payment-code" label="Betalningskod (vad avser betalningen?)"
                               value={paymentCode} onChange={setPaymentCode}/>
          </>
        : <>
            <SelectInput label="Betalas till" required={true} options={ROUTES} valueGetter={r => r.value}
              nameGetter={r => r.name} value={route} onChange={setRoute}/>
            {route === 'bank' ? <>
              <ClearingInput id="new-supplier-clearing" label="Clearingnummer" value={clearing} onChange={setClearing}/>
              <BankAccountNumberInput id="new-supplier-account" label="Kontonummer" value={account} onChange={setAccount}/>
              {showBankStatus &&
                <div style={{ fontSize: '0.8rem', color: bankCheck.ok ? '#2e7d32' : '#c62828' }}>
                  {bankCheck.ok ? `✓ ${bankCheck.bank}` : bankCheck.reason}
                </div>}
            </> : <GiroInput id="new-giro-number-tramse" label='Bank-/plusgiro' value={giroNumber} onChange={setGiroNumber}/>}
          </>}
      <div className="buttons">
          <Button variant="contained" disabled={!isValid} id="button-submit-create-supplier" onClick={createSupplier}>Skapa leverantör</Button>
          <Button variant="text" id="button-cancel-popup" onClick={onCancel}>Avbryt</Button>
      </div>
    </div>
    </>
  )
}

export default SupplierCreationDialog
